import React from 'react';

export interface LevelTier {
  id: string;
  xp: number;
  title: string;
  tifinagh: string;
  desc: string;
  quote: string;
  themeColor: string;
  borderClass: string;
  glowColor: string;
  accentBg: string;
  bgGradient: string;
  tierNumber: number;
}

export const LEVEL_THRESHOLDS: LevelTier[] = [
  {
    id: 'aqcic',
    xp: 0,
    title: 'Aqcic',
    tifinagh: 'ⴰⵇⵛⵉⵛ',
    desc: 'Enfant du Village',
    quote: '« Chaque grand chêne a d’abord été un gland tombé sur la terre des ancêtres. »',
    themeColor: '#10b981',
    borderClass: 'border-emerald-500/80',
    glowColor: 'rgba(16, 185, 129, 0.4)',
    accentBg: 'from-emerald-500/20 via-emerald-600/10 to-transparent',
    bgGradient: 'from-emerald-950 via-stone-900 to-emerald-950',
    tierNumber: 1,
  },
  {
    id: 'ameksa',
    xp: 500,
    title: 'Ameksa',
    tifinagh: 'ⴰⵎⴻⴽⵙⴰ',
    desc: 'Le Berger Curieux',
    quote: '« Celui qui gravit les collines avec son troupeau entend chanter la montagne. »',
    themeColor: '#eab308',
    borderClass: 'border-amber-500/80',
    glowColor: 'rgba(234, 179, 8, 0.4)',
    accentBg: 'from-amber-500/20 via-yellow-600/10 to-transparent',
    bgGradient: 'from-amber-950 via-stone-900 to-amber-950',
    tierNumber: 2,
  },
  {
    id: 'amnay',
    xp: 1500,
    title: 'Amnay',
    tifinagh: 'ⴰⵎⵏⴰⵢ',
    desc: 'Le Cavalier Courageux',
    quote: '« Le vent n’effraie pas celui qui chevauche le cœur vaillant. »',
    themeColor: '#f97316',
    borderClass: 'border-orange-500/80',
    glowColor: 'rgba(249, 115, 22, 0.4)',
    accentBg: 'from-orange-500/20 via-amber-600/10 to-transparent',
    bgGradient: 'from-orange-950 via-stone-900 to-orange-950',
    tierNumber: 3,
  },
  {
    id: 'amusnaw',
    xp: 3500,
    title: 'Amusnaw',
    tifinagh: 'ⴰⵎⵓⵙⵏⴰⵡ',
    desc: 'Le Chercheur de Savoir',
    quote: '« Le savoir est une fontaine inépuisable où chaque mot est une goutte d’or. »',
    themeColor: '#0ea5e9',
    borderClass: 'border-sky-500/80',
    glowColor: 'rgba(14, 165, 233, 0.4)',
    accentBg: 'from-sky-500/20 via-blue-600/10 to-transparent',
    bgGradient: 'from-sky-950 via-stone-900 to-sky-950',
    tierNumber: 4,
  },
  {
    id: 'amussnu',
    xp: 7000,
    title: 'Amussnu',
    tifinagh: 'ⴰⵎⵓⵙⵙⵏⵓ',
    desc: 'Le Savant Éclairé',
    quote: '« La véritable connaissance éclaire la nuit de ceux qui cherchent leur chemin. »',
    themeColor: '#a855f7',
    borderClass: 'border-purple-500/80',
    glowColor: 'rgba(168, 85, 247, 0.4)',
    accentBg: 'from-purple-500/20 via-indigo-600/10 to-transparent',
    bgGradient: 'from-purple-950 via-stone-900 to-purple-950',
    tierNumber: 5,
  },
  {
    id: 'argaz',
    xp: 12000,
    title: 'Argaz / Tameṭṭut',
    tifinagh: 'ⴰⵔⴳⴰⵣ • ⵜⴰⵎⴻⵟⵟⵓⵜ',
    desc: 'Le Pilier Accompli',
    quote: '« Le pilier de la maison ne ploie jamais face aux tempêtes de l’hiver. »',
    themeColor: '#ef4444',
    borderClass: 'border-rose-500/80',
    glowColor: 'rgba(239, 68, 68, 0.4)',
    accentBg: 'from-rose-500/20 via-red-600/10 to-transparent',
    bgGradient: 'from-rose-950 via-stone-900 to-rose-950',
    tierNumber: 6,
  },
  {
    id: 'amghar',
    xp: 20000,
    title: 'Amɣar Azemni',
    tifinagh: 'ⴰⵎⵖⴰⵔ ⴰⵣⴻⵎⵏⵉ',
    desc: 'Le Grand Sage & Gardien',
    quote: '« Celui qui possède la mémoire des mots règne en paix sur les siècles. »',
    themeColor: '#fbbf24',
    borderClass: 'border-amber-400',
    glowColor: 'rgba(251, 191, 36, 0.6)',
    accentBg: 'from-amber-400/30 via-yellow-500/20 to-transparent',
    bgGradient: 'from-amber-950 via-stone-900 to-yellow-950',
    tierNumber: 7,
  }
];

interface TierEmblemProps {
  tierId: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'hero';
  isUnlocked?: boolean;
  isCurrent?: boolean;
}

export const TierEmblem: React.FC<TierEmblemProps> = ({
  tierId,
  size = 'md',
  isUnlocked = true,
  isCurrent = false,
}) => {
  const dimensionClass = {
    xs: 'w-8 h-8',
    sm: 'w-10 h-10',
    md: 'w-14 h-14',
    lg: 'w-20 h-20',
    hero: 'w-28 h-28 sm:w-32 sm:h-32',
  }[size];

  // Specific custom emblem graphics for each tier with authentic Berber geometry
  const renderEmblemArtwork = () => {
    switch (tierId) {
      case 'aqcic':
        return (
          // Pousse d'olivier dans un losange d'argile traditionnel berbère
          <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-md">
            <defs>
              <linearGradient id="aqcicGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#34d399" />
                <stop offset="100%" stopColor="#059669" />
              </linearGradient>
              <linearGradient id="clayGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#d97706" />
                <stop offset="100%" stopColor="#78350f" />
              </linearGradient>
            </defs>
            {/* Losange Kabyle de fond */}
            <polygon points="50,5 95,50 50,95 5,50" fill="url(#clayGrad)" stroke="#10b981" strokeWidth="2.5" />
            <polygon points="50,14 86,50 50,86 14,50" fill="#064e3b" opacity="0.85" />
            {/* Motifs géométriques aux 4 coins */}
            <circle cx="50" cy="18" r="2.5" fill="#fef08a" />
            <circle cx="82" cy="50" r="2.5" fill="#fef08a" />
            <circle cx="50" cy="82" r="2.5" fill="#fef08a" />
            <circle cx="18" cy="50" r="2.5" fill="#fef08a" />
            {/* Rameau / Jeune pousse d'olivier */}
            <path d="M50,76 Q50,45 50,26" stroke="#34d399" strokeWidth="3.5" strokeLinecap="round" fill="none" />
            <path d="M50,58 Q34,50 36,40 Q46,44 50,50" fill="url(#aqcicGrad)" stroke="#065f46" strokeWidth="1" />
            <path d="M50,50 Q66,42 64,32 Q54,36 50,42" fill="url(#aqcicGrad)" stroke="#065f46" strokeWidth="1" />
            <path d="M50,38 Q36,30 40,22 Q48,26 50,30" fill="url(#aqcicGrad)" stroke="#065f46" strokeWidth="1" />
            <path d="M50,30 Q64,22 60,14 Q52,18 50,22" fill="url(#aqcicGrad)" stroke="#065f46" strokeWidth="1" />
            {/* Étoile de départ */}
            <circle cx="50" cy="24" r="3" fill="#fef08a" />
          </svg>
        );

      case 'ameksa':
        return (
          // Cornes de bélier (Isken) et soleil des collines kabyles
          <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-md">
            <defs>
              <linearGradient id="ameksaGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fde047" />
                <stop offset="50%" stopColor="#eab308" />
                <stop offset="100%" stopColor="#a16207" />
              </linearGradient>
            </defs>
            {/* Bouclier hexagonal de berger */}
            <polygon points="50,6 90,26 90,74 50,94 10,74 10,26" fill="#451a03" stroke="#eab308" strokeWidth="3" />
            <polygon points="50,14 82,30 82,70 50,86 18,70 18,30" fill="#78350f" opacity="0.9" />
            {/* Soleil central */}
            <circle cx="50" cy="46" r="14" fill="url(#ameksaGrad)" stroke="#ca8a04" strokeWidth="1.5" />
            <circle cx="50" cy="46" r="8" fill="#fef08a" />
            {/* Motifs des cornes de bélier kabyles (symbole de force et protection du troupeau) */}
            <path d="M50,46 C38,28 22,34 24,48 C25,58 38,54 42,48" fill="none" stroke="#fde047" strokeWidth="3.5" strokeLinecap="round" />
            <path d="M50,46 C62,28 78,34 76,48 C75,58 62,54 58,48" fill="none" stroke="#fde047" strokeWidth="3.5" strokeLinecap="round" />
            {/* Bâton de berger stylisé */}
            <path d="M50,22 L50,80" stroke="#fef08a" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx="50" cy="20" r="3" fill="#facc15" />
            {/* Tissage inférieur */}
            <polygon points="42,76 50,68 58,76 50,84" fill="#ca8a04" />
          </svg>
        );

      case 'amnay':
        return (
          // Cavalier, fer à cheval & pointe de lance guerrière berbère aux émaux corail
          <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-md">
            <defs>
              <linearGradient id="amnayGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fb923c" />
                <stop offset="100%" stopColor="#c2410c" />
              </linearGradient>
            </defs>
            {/* Écu guerrier à chevrons */}
            <path d="M50,6 L88,18 C88,60 50,94 50,94 C50,94 12,60 12,18 Z" fill="#7c2d12" stroke="#f97316" strokeWidth="3" />
            <path d="M50,14 L80,24 C80,56 50,84 50,84 C50,84 20,56 20,24 Z" fill="#9a3412" opacity="0.8" />
            {/* Lance berbère centrale */}
            <polygon points="50,16 57,36 50,32 43,36" fill="#fed7aa" stroke="#c2410c" strokeWidth="1" />
            <line x1="50" y1="32" x2="50" y2="78" stroke="#fdba74" strokeWidth="3" strokeLinecap="round" />
            {/* Deux fers à cheval / ailes de bravoure */}
            <path d="M30,36 C24,48 26,62 38,68 C44,70 48,64 44,58 C38,54 36,46 40,40" fill="url(#amnayGrad)" stroke="#ea580c" strokeWidth="1.5" />
            <path d="M70,36 C76,48 74,62 62,68 C56,70 52,64 56,58 C62,54 64,46 60,40" fill="url(#amnayGrad)" stroke="#ea580c" strokeWidth="1.5" />
            {/* Rubis / Corail central */}
            <circle cx="50" cy="50" r="5.5" fill="#ef4444" stroke="#fed7aa" strokeWidth="1.5" />
          </svg>
        );

      case 'amusnaw':
        return (
          // Tabzimt d'argent (Fibule Kabyle) et livre de sagesse antique
          <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-md">
            <defs>
              <linearGradient id="amusnawGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#38bdf8" />
                <stop offset="50%" stopColor="#0284c7" />
                <stop offset="100%" stopColor="#0369a1" />
              </linearGradient>
            </defs>
            {/* Étoile octogonale de la connaissance */}
            <polygon points="50,4 63,16 80,10 80,28 96,37 88,54 96,70 80,78 74,96 56,90 44,96 26,90 20,78 4,70 12,54 4,37 20,28 20,10 37,16" fill="#082f49" stroke="#38bdf8" strokeWidth="2.5" />
            <circle cx="50" cy="50" r="30" fill="#0c4a6e" stroke="#0ea5e9" strokeWidth="2" />
            {/* Fibule Kabyle circulaire (Tabzimt) */}
            <circle cx="50" cy="50" r="22" fill="none" stroke="#7dd3fc" strokeWidth="3" strokeDasharray="6,3" />
            {/* Aiguille de la fibule */}
            <line x1="30" y1="70" x2="70" y2="30" stroke="#f0f9ff" strokeWidth="3" strokeLinecap="round" />
            {/* Émail central bleu saphir */}
            <polygon points="50,34 62,50 50,66 38,50" fill="url(#amusnawGrad)" stroke="#e0f2fe" strokeWidth="1.5" />
            <circle cx="50" cy="50" r="4.5" fill="#facc15" />
            {/* Perles de corail aux 4 points cardinaux */}
            <circle cx="50" cy="22" r="3" fill="#ef4444" />
            <circle cx="78" cy="50" r="3" fill="#ef4444" />
            <circle cx="50" cy="78" r="3" fill="#ef4444" />
            <circle cx="22" cy="50" r="3" fill="#ef4444" />
          </svg>
        );

      case 'amussnu':
        return (
          // Œil de sagesse et diadème d'Ath Yenni en émaux tricolores
          <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-md">
            <defs>
              <linearGradient id="amussnuGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#c084fc" />
                <stop offset="50%" stopColor="#9333ea" />
                <stop offset="100%" stopColor="#581c87" />
              </linearGradient>
            </defs>
            {/* Grand médaillon royal aux 12 rayons */}
            <circle cx="50" cy="50" r="44" fill="#3b0764" stroke="#c084fc" strokeWidth="3" />
            <circle cx="50" cy="50" r="36" fill="#581c87" stroke="#a855f7" strokeWidth="1.5" />
            {/* Losange central aux 3 couleurs berbères (Bleu ciel, Vert plante, Jaune soleil) */}
            <polygon points="50,18 78,50 50,82 22,50" fill="url(#amussnuGrad)" stroke="#e9d5ff" strokeWidth="2" />
            {/* L'Œil de la Sagesse ancestrale */}
            <path d="M30,50 Q50,30 70,50 Q50,70 30,50 Z" fill="#2e1065" stroke="#facc15" strokeWidth="2.5" />
            <circle cx="50" cy="50" r="8" fill="#facc15" stroke="#10b981" strokeWidth="2" />
            <circle cx="50" cy="50" r="4" fill="#0f172a" />
            <circle cx="52" cy="48" r="1.5" fill="#ffffff" />
            {/* Motifs émaillés aux angles */}
            <circle cx="50" cy="26" r="3.5" fill="#10b981" stroke="#fef08a" strokeWidth="1" />
            <circle cx="74" cy="50" r="3.5" fill="#3b82f6" stroke="#fef08a" strokeWidth="1" />
            <circle cx="50" cy="74" r="3.5" fill="#ef4444" stroke="#fef08a" strokeWidth="1" />
            <circle cx="26" cy="50" r="3.5" fill="#3b82f6" stroke="#fef08a" strokeWidth="1" />
          </svg>
        );

      case 'argaz':
        return (
          // Le grand YAZ berbère (ⵣ) sculpté en or flamboyant et corail
          <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-md">
            <defs>
              <linearGradient id="yazGold" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fef08a" />
                <stop offset="30%" stopColor="#f59e0b" />
                <stop offset="100%" stopColor="#b45309" />
              </linearGradient>
              <linearGradient id="shieldRed" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#881337" />
                <stop offset="50%" stopColor="#4c0519" />
                <stop offset="100%" stopColor="#1e0208" />
              </linearGradient>
            </defs>
            {/* Grand bouclier d'argent ciselé et corail */}
            <polygon points="50,4 92,20 84,78 50,96 16,78 8,20" fill="url(#shieldRed)" stroke="#f43f5e" strokeWidth="3" />
            <polygon points="50,12 84,24 76,72 50,88 24,72 16,24" fill="#7f1d1d" opacity="0.85" stroke="#fb7185" strokeWidth="1.5" />
            {/* Symbole YAZ berbère central (ⵣ - L'Homme Libre) */}
            {/* Arc supérieur */}
            <path d="M30,30 C30,16 70,16 70,30" fill="none" stroke="url(#yazGold)" strokeWidth="6" strokeLinecap="round" />
            {/* Mât central */}
            <line x1="50" y1="22" x2="50" y2="78" stroke="url(#yazGold)" strokeWidth="6" strokeLinecap="round" />
            {/* Arc inférieur */}
            <path d="M30,70 C30,84 70,84 70,70" fill="none" stroke="url(#yazGold)" strokeWidth="6" strokeLinecap="round" />
            {/* Coraux précieux d'Ath Yenni */}
            <circle cx="50" cy="50" r="6" fill="#ef4444" stroke="#fef08a" strokeWidth="2" />
            <circle cx="30" cy="30" r="3.5" fill="#ef4444" />
            <circle cx="70" cy="30" r="3.5" fill="#ef4444" />
            <circle cx="30" cy="70" r="3.5" fill="#ef4444" />
            <circle cx="70" cy="70" r="3.5" fill="#ef4444" />
          </svg>
        );

      case 'amghar':
      default:
        return (
          // Couronne de Tajmaɛt d'or pur, rayons solaires et sceau des Anciens
          <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-lg">
            <defs>
              <linearGradient id="amgharGold" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fffbeb" />
                <stop offset="25%" stopColor="#fde047" />
                <stop offset="60%" stopColor="#eab308" />
                <stop offset="100%" stopColor="#854d0e" />
              </linearGradient>
              <radialGradient id="sunGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#fef08a" stopOpacity="1" />
                <stop offset="60%" stopColor="#ca8a04" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#451a03" stopOpacity="0.9" />
              </radialGradient>
            </defs>
            {/* Halo solaire et rayons divins */}
            <circle cx="50" cy="50" r="46" fill="url(#sunGlow)" stroke="#fde047" strokeWidth="3" />
            {/* 16 Rayons géométriques de la sagesse */}
            <g stroke="#fef08a" strokeWidth="2" opacity="0.75">
              <line x1="50" y1="4" x2="50" y2="14" />
              <line x1="50" y1="86" x2="50" y2="96" />
              <line x1="4" y1="50" x2="14" y2="50" />
              <line x1="86" y1="50" x2="96" y2="50" />
              <line x1="17" y1="17" x2="24" y2="24" />
              <line x1="76" y1="76" x2="83" y2="83" />
              <line x1="17" y1="83" x2="24" y2="76" />
              <line x1="76" y1="24" x2="83" y2="17" />
            </g>
            {/* Diadème Royal Berbère (Taɛsṣabt) */}
            <path d="M20,62 L26,38 L38,48 L50,28 L62,48 L74,38 L80,62 Z" fill="url(#amgharGold)" stroke="#fef08a" strokeWidth="2.5" />
            <polygon points="50,28 50,44 42,40" fill="#fef08a" opacity="0.6" />
            <polygon points="50,28 50,44 58,40" fill="#a16207" opacity="0.6" />
            {/* Émeraudes, Saphirs et Rubis sertis */}
            <circle cx="50" cy="28" r="4.5" fill="#ef4444" stroke="#fef08a" strokeWidth="1.5" />
            <circle cx="26" cy="38" r="3.5" fill="#10b981" stroke="#fef08a" strokeWidth="1" />
            <circle cx="74" cy="38" r="3.5" fill="#10b981" stroke="#fef08a" strokeWidth="1" />
            <circle cx="38" cy="48" r="3" fill="#3b82f6" stroke="#fef08a" strokeWidth="1" />
            <circle cx="62" cy="48" r="3" fill="#3b82f6" stroke="#fef08a" strokeWidth="1" />
            {/* Sceau doré de Tajmaɛt */}
            <circle cx="50" cy="72" r="11" fill="#713f12" stroke="#fef08a" strokeWidth="2" />
            {/* Yaz miniature gravé */}
            <path d="M44,67 C44,64 56,64 56,67" stroke="#fef08a" strokeWidth="2" fill="none" strokeLinecap="round" />
            <line x1="50" y1="65" x2="50" y2="79" stroke="#fef08a" strokeWidth="2" strokeLinecap="round" />
            <path d="M44,77 C44,80 56,80 56,77" stroke="#fef08a" strokeWidth="2" fill="none" strokeLinecap="round" />
            <circle cx="50" cy="72" r="2" fill="#ef4444" />
          </svg>
        );
    }
  };

  return (
    <div className={`relative ${dimensionClass} flex items-center justify-center shrink-0 select-none ${
      !isUnlocked ? 'grayscale opacity-40' : ''
    }`}>
      {/* Halo radiant pour le niveau actuel */}
      {isCurrent && (
        <div 
          className="absolute -inset-2 rounded-full animate-pulse blur-sm"
          style={{
            background: `radial-gradient(circle, ${
              LEVEL_THRESHOLDS.find((t) => t.id === tierId)?.glowColor || 'rgba(245, 158, 11, 0.5)'
            } 0%, transparent 70%)`
          }}
        />
      )}

      {/* Cadre de l'emblème */}
      <div className="w-full h-full relative z-10 flex items-center justify-center transform transition-transform hover:scale-105 duration-300">
        {renderEmblemArtwork()}
      </div>
    </div>
  );
};
